import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Handshake,
  Plus,
  Trash2,
  Pencil,
  Upload,
  Search,
  Printer,
} from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { printHtmlDirect } from "@/lib/directPrint";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SearchableCombobox } from "@/components/ui/searchable-combobox";

const eur = (v) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(
    Number(v) || 0,
  );
const vuoto = () => ({
  nome: "",
  cliente: "",
  descrizione: "",
  data_inizio: new Date().toISOString().slice(0, 10),
  stato: "In corso",
});
const spesaVuota = () => ({
  data: new Date().toISOString().slice(0, 10),
  descrizione: "",
  importo: "",
  fornitore: "",
  numero_documento: "",
  note: "",
});

export default function CollaborazioniPage() {
  const navigate = useNavigate();
  const annoCorrente = String(new Date().getFullYear());
  const [items, setItems] = useState([]),
    [selected, setSelected] = useState(null),
    [detail, setDetail] = useState(null);
  const [form, setForm] = useState(vuoto()),
    [editing, setEditing] = useState(false),
    [tab, setTab] = useState("materiali"),
    [anno, setAnno] = useState(annoCorrente);
  const [spesa, setSpesa] = useState(spesaVuota()),
    [ddtFile, setDdtFile] = useState(null),
    [preview, setPreview] = useState(null),
    [busy, setBusy] = useState(false),
    [fornitore, setFornitore] = useState("");
  const [lavoro, setLavoro] = useState({
      ...spesaVuota(),
      cantiere_id: "",
      lavorazione_id: "",
    }),
    [cantieri, setCantieri] = useState([]),
    [lavorazioni, setLavorazioni] = useState([]);
  const load = async () => {
    const r = await api.get("/collaborazioni");
    setItems(r.data);
    return r.data;
  };
  const loadDetail = async (id, year = anno) => {
    const r = await api.get(`/collaborazioni/${id}/dettaglio`, {
      params: year ? { anno: year } : {},
    });
    setDetail(r.data);
  };
  useEffect(() => {
    load();
    api
      .get("/cantieri")
      .then((r) =>
        setCantieri((r.data || []).filter((x) => x.stato !== "Completato")),
      )
      .catch(() => setCantieri([]));
  }, []);
  useEffect(() => {
    document
      .querySelectorAll('input[placeholder="Importo"]')
      .forEach((input) => {
        input.placeholder = "Importo €";
      });
  }, [tab]);
  const scegli = (x) => {
    setSelected(x);
    setForm({ ...vuoto(), ...x });
    setEditing(false);
    setAnno(annoCorrente);
    setTab("materiali");
    loadDetail(x.id, annoCorrente);
  };
  const crea = async (e) => {
    e.preventDefault();
    await api.post("/collaborazioni", form);
    setForm(vuoto());
    await load();
    toast.success("Collaborazione creata");
  };
  const salvaModifica = async () => {
    await api.put(`/collaborazioni/${selected.id}`, form);
    const elenco = await load();
    const nuovo = elenco.find((x) => x.id === selected.id);
    setSelected(nuovo);
    setEditing(false);
    await loadDetail(selected.id);
    toast.success("Collaborazione aggiornata");
  };
  const elimina = async () => {
    if (
      !window.confirm(
        `Eliminare la collaborazione "${selected.nome}" e tutti i dati collegati?`,
      )
    )
      return;
    await api.delete(`/collaborazioni/${selected.id}`);
    setSelected(null);
    setDetail(null);
    await load();
    toast.success("Collaborazione eliminata");
  };
  const aggiungiSpesa = async () => {
    if (!spesa.descrizione || !spesa.importo)
      return toast.error("Inserisci descrizione e importo");
    await api.post(`/collaborazioni/${selected.id}/spese`, {
      ...spesa,
      importo: Number(String(spesa.importo).replace(",", ".")),
    });
    setSpesa(spesaVuota());
    await loadDetail(selected.id);
    await load();
    toast.success("Spesa registrata");
  };
  const eliminaSpesa = async (id) => {
    if (!window.confirm("Eliminare la spesa manuale?")) return;
    await api.delete(`/collaborazioni-movimenti/spese/${id}`);
    await loadDetail(selected.id);
    await load();
  };
  const scegliCantiereLavoro = async (cantiere_id) => {
    setLavoro((x) => ({ ...x, cantiere_id, lavorazione_id: "" }));
    if (!cantiere_id) {
      setLavorazioni([]);
      return;
    }
    try {
      const r = await api.get(`/cantieri/${cantiere_id}/lavorazioni`);
      setLavorazioni(r.data || []);
    } catch (_) {
      setLavorazioni([]);
    }
  };
  const aggiungiLavoro = async () => {
    if (!lavoro.descrizione || !lavoro.importo || !lavoro.cantiere_id)
      return toast.error("Inserisci data, descrizione, importo e cantiere");
    await api.post(`/collaborazioni/${selected.id}/lavori`, {
      ...lavoro,
      lavorazione_id: lavoro.lavorazione_id || null,
      importo: Number(String(lavoro.importo).replace(",", ".")),
    });
    setLavoro({ ...spesaVuota(), cantiere_id: "", lavorazione_id: "" });
    setLavorazioni([]);
    await loadDetail(selected.id);
    toast.success(
      "Lavoro registrato anche nei materiali e nella lavorazione del cantiere",
    );
  };
  const eliminaLavoro = async (id) => {
    if (
      !window.confirm("Eliminare il lavoro anche dai materiali del cantiere?")
    )
      return;
    await api.delete(`/collaborazioni-movimenti/lavori/${id}`);
    await loadDetail(selected.id);
    toast.success("Lavoro eliminato");
  };
  const analizza = async () => {
    if (!ddtFile) return toast.error("Seleziona un PDF o un'immagine");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", ddtFile);
      const r = await api.post(
        `/cantieri/collab:${selected.id}/materiali/importa-ddt/anteprima`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setPreview(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const cambiaRiga = (i, k, v) =>
    setPreview((p) => ({
      ...p,
      righe: p.righe.map((r, j) => (j === i ? { ...r, [k]: v } : r)),
    }));
  const confermaDdt = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/cantieri/collab:${selected.id}/materiali/importa-ddt/conferma`,
        {
          token: preview.token,
          righe: preview.righe.map((x) => ({
            ...x,
            costo_totale: Number(x.costo_totale) || 0,
          })),
        },
      );
      toast.success(`${r.data.importati} DDT importati`);
      setPreview(null);
      setDdtFile(null);
      await loadDetail(selected.id);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  const confronto = (detail?.confronti || []).find(
    (x) => x.fornitore === fornitore,
  );
  const stampaRendiconto = () => {
    if (!detail) return;
    const rows = (v, t) =>
      (v || [])
        .map(
          (x) =>
            `<tr><td>${x.data || x.data_acquisto || ""}</td><td>${x.numero_documento || x.numero_ddt || "-"}</td><td>${x.descrizione || x.nome || ""}</td><td>${t === "lavori" ? x.cantiere_nome || "" : x.fornitore || ""}</td><td class="n">${eur(x.importo ?? x.costo_totale)}</td></tr>`,
        )
        .join("") || '<tr><td colspan="5">Nessuna voce</td></tr>';
    const table = (titolo, v, t, tot) =>
      `<h2>${titolo}</h2><table><thead><tr><th>Data</th><th>Documento</th><th>Descrizione</th><th>${t === "lavori" ? "Cantiere" : "Riferimento"}</th><th>Importo</th></tr></thead><tbody>${rows(v, t)}</tbody><tfoot><tr><td colspan="4">Totale ${titolo}</td><td class="n">${eur(tot)}</td></tr></tfoot></table>`;
    const coloreDare =
      Number(detail.rendiconto) > 0
        ? "rosso"
        : Number(detail.rendiconto) < 0
          ? "verde"
          : "";
    const h = `<!doctype html><html><head><style>@page{size:A4 landscape;margin:12mm}body{font-family:Arial;color:#111}table{width:100%;border-collapse:collapse;font-size:12px;margin-bottom:20px}th,td{border:1px solid #aaa;padding:7px}th,tfoot{background:#eee;font-weight:bold}.n{text-align:right}.tot{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;border:2px solid #111;padding:14px}.tot b{display:block;font-size:18px;margin-top:4px}.rosso{color:#dc2626}.verde{color:#16a34a}</style></head><body><h1>Collaborazione: ${selected.nome}</h1><p>Anno: ${anno || "Tutti gli anni"}</p>${table("Materiale", detail.materiali, "materiali", detail.totale_materiali)}${table("Acconti", detail.spese, "acconti", detail.totale_spese)}${table("Lavori", detail.lavori, "lavori", detail.totale_lavori)}<div class="tot"><div>Materiale<b>${eur(detail.totale_materiali)}</b></div><div>Acconti<b>${eur(detail.totale_spese)}</b></div><div>Lavori<b>${eur(detail.totale_lavori)}</b></div><div>Dare: Lavori − Acconti − Materiale<b class="${coloreDare}">${eur(detail.rendiconto)}</b></div></div></body></html>`;
    printHtmlDirect(h, `Rendiconto ${selected.nome}`);
  };
  return (
    <AppShell>
      <button
        onClick={() => navigate("/gestionale")}
        className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-primary mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Gestionale
      </button>
      <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground flex items-center gap-2">
        <Handshake className="h-4 w-4" />
        Gestione economica
      </p>
      <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mt-2">
        Collaborazioni
      </h1>
      <div className="grid lg:grid-cols-[330px_1fr] gap-6 mt-8">
        <div className="space-y-5">
          <form
            onSubmit={crea}
            className="rounded-xl border bg-card p-5 space-y-3"
          >
            <h2 className="font-heading text-xl font-bold">
              Nuova collaborazione
            </h2>
            <Input
              placeholder="Nome collaborazione"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              required
            />
            <Input
              placeholder="Cliente"
              value={form.cliente}
              onChange={(e) => setForm({ ...form, cliente: e.target.value })}
            />
            <Input
              type="date"
              value={form.data_inizio || ""}
              onChange={(e) =>
                setForm({ ...form, data_inizio: e.target.value })
              }
            />
            <Textarea
              placeholder="Descrizione"
              value={form.descrizione || ""}
              onChange={(e) =>
                setForm({ ...form, descrizione: e.target.value })
              }
            />
            <Button type="submit">
              <Plus className="h-4 w-4 mr-2" />
              Crea
            </Button>
          </form>
          <div className="space-y-2">
            {items.map((x) => (
              <button
                data-global-search-item
                key={x.id}
                onClick={() => scegli(x)}
                className={`w-full text-left rounded-xl border p-4 ${selected?.id === x.id ? "border-primary bg-primary/5" : "bg-card"}`}
              >
                <p className="font-bold">{x.nome}</p>
                <p className="text-sm text-muted-foreground">
                  {x.cliente || "Nessun cliente"}
                </p>
                <p
                  className={`text-sm mt-2 font-semibold ${Number(x.rendiconto) > 0 ? "text-destructive" : Number(x.rendiconto) < 0 ? "text-emerald-600" : ""}`}
                >
                  Dare {eur(x.rendiconto)}
                </p>
              </button>
            ))}
          </div>
        </div>
        {!selected ? (
          <div className="rounded-xl border border-dashed p-12 text-center text-muted-foreground">
            Seleziona o crea una collaborazione.
          </div>
        ) : (
          <div className="space-y-5">
            <div className="rounded-xl border bg-card p-5">
              {editing ? (
                <div className="grid sm:grid-cols-2 gap-3">
                  <Input
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                  />
                  <Input
                    value={form.cliente || ""}
                    onChange={(e) =>
                      setForm({ ...form, cliente: e.target.value })
                    }
                  />
                  <Textarea
                    className="sm:col-span-2"
                    value={form.descrizione || ""}
                    onChange={(e) =>
                      setForm({ ...form, descrizione: e.target.value })
                    }
                  />
                  <div className="flex gap-2">
                    <Button onClick={salvaModifica}>Salva modifiche</Button>
                    <Button variant="outline" onClick={() => setEditing(false)}>
                      Annulla
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <h2 className="font-heading text-2xl font-bold">
                      {selected.nome}
                    </h2>
                    <p className="text-muted-foreground">{selected.cliente}</p>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={stampaRendiconto}>
                      <Printer className="h-4 w-4 mr-2" />
                      Stampa rendiconto
                    </Button>
                    <Button variant="outline" onClick={() => setEditing(true)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Modifica
                    </Button>
                    <Button
                      variant="outline"
                      className="text-destructive"
                      onClick={elimina}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Elimina
                    </Button>
                  </div>
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-3 gap-4">
              <Stat label="Materiale" value={detail?.totale_materiali} />
              <Stat label="Acconti" value={detail?.totale_spese} />
              <Stat
                label="Dare: Lavori − Acconti − Materiale"
                value={detail?.rendiconto}
                strong
              />
            </div>
            <div className="flex flex-wrap gap-2 border-b pb-3">
              {[
                ["materiali", "Materiale"],
                ["spese", "Acconti"],
                ["lavori", "Lavori"],
              ].map(([k, l]) => (
                <Button
                  key={k}
                  variant={tab === k ? "default" : "outline"}
                  onClick={() => setTab(k)}
                >
                  {l}
                </Button>
              ))}
              <select
                value={anno}
                onChange={(e) => {
                  setAnno(e.target.value);
                  loadDetail(selected.id, e.target.value);
                }}
                className="h-10 rounded-md border bg-background px-3 ml-auto"
              >
                <option value="">Tutti gli anni</option>
                {[2024, 2025, 2026, 2027].map((x) => (
                  <option key={x}>{x}</option>
                ))}
              </select>
            </div>
            {tab === "materiali" && (
              <Movimenti rows={detail?.materiali || []} />
            )}{" "}
            {tab === "spese" && (
              <>
                <div className="rounded-xl border bg-card p-5">
                  <h3 className="font-bold mb-3">Aggiungi acconto manuale</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    <Input
                      type="date"
                      value={spesa.data}
                      onChange={(e) =>
                        setSpesa({ ...spesa, data: e.target.value })
                      }
                    />
                    <Input
                      placeholder="Descrizione"
                      value={spesa.descrizione}
                      onChange={(e) =>
                        setSpesa({ ...spesa, descrizione: e.target.value })
                      }
                    />
                    <Input
                      placeholder="Importo"
                      value={spesa.importo}
                      onChange={(e) =>
                        setSpesa({ ...spesa, importo: e.target.value })
                      }
                    />
                    <Input
                      placeholder="Riferimento"
                      value={spesa.fornitore}
                      onChange={(e) =>
                        setSpesa({ ...spesa, fornitore: e.target.value })
                      }
                    />
                  </div>
                  <Button className="mt-3" onClick={aggiungiSpesa}>
                    Registra acconto
                  </Button>
                </div>
                <Movimenti rows={detail?.spese || []} elimina={eliminaSpesa} />
              </>
            )}
            {tab === "lavori" && (
              <>
                <div className="rounded-xl border bg-card p-5">
                  <h3 className="font-bold mb-3">
                    Aggiungi lavoro a un cantiere
                  </h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
                    <Input
                      type="date"
                      value={lavoro.data}
                      onChange={(e) =>
                        setLavoro({ ...lavoro, data: e.target.value })
                      }
                    />
                    <Input
                      placeholder="Descrizione lavoro"
                      value={lavoro.descrizione}
                      onChange={(e) =>
                        setLavoro({ ...lavoro, descrizione: e.target.value })
                      }
                    />
                    <Input
                      placeholder="Importo"
                      value={lavoro.importo}
                      onChange={(e) =>
                        setLavoro({ ...lavoro, importo: e.target.value })
                      }
                    />
                    <SearchableCombobox
                      value={lavoro.cantiere_id}
                      onValueChange={scegliCantiereLavoro}
                      options={cantieri.map((x) => ({
                        value: x.id,
                        label: x.nome,
                      }))}
                      placeholder="Seleziona cantiere"
                      searchPlaceholder="Scrivi il cantiere..."
                    />
                    <select
                      value={lavoro.lavorazione_id}
                      onChange={(e) =>
                        setLavoro({ ...lavoro, lavorazione_id: e.target.value })
                      }
                      disabled={!lavoro.cantiere_id}
                      className="h-10 rounded-md border bg-background px-3 disabled:opacity-50"
                    >
                      <option value="">Nessuna lavorazione</option>
                      {lavorazioni.map((x) => (
                        <option key={x.id} value={x.id}>
                          {x.nome}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Button className="mt-3" onClick={aggiungiLavoro}>
                    Registra lavoro
                  </Button>
                </div>
                <Movimenti
                  rows={detail?.lavori || []}
                  elimina={eliminaLavoro}
                  lavori
                />
              </>
            )}
            {tab === "ddt" && (
              <>
                <div className="rounded-xl border bg-card p-5">
                  <h3 className="font-bold mb-3">Importa DDT</h3>
                  <div className="flex flex-wrap gap-3">
                    <Input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png,.webp"
                      onChange={(e) => setDdtFile(e.target.files?.[0])}
                      className="max-w-md"
                    />
                    <Button onClick={analizza} disabled={busy}>
                      <Upload className="h-4 w-4 mr-2" />
                      Analizza documento
                    </Button>
                  </div>
                  {preview && (
                    <div className="mt-5 space-y-3">
                      {preview.righe.map((r, i) => (
                        <div
                          key={r.documento_id}
                          className="grid sm:grid-cols-2 lg:grid-cols-5 gap-2 border rounded-lg p-3"
                        >
                          <Input
                            value={r.numero_ddt}
                            onChange={(e) =>
                              cambiaRiga(i, "numero_ddt", e.target.value)
                            }
                            placeholder="Numero DDT"
                          />
                          <Input
                            type="date"
                            value={r.data_acquisto}
                            onChange={(e) =>
                              cambiaRiga(i, "data_acquisto", e.target.value)
                            }
                          />
                          <Input
                            value={r.fornitore}
                            onChange={(e) =>
                              cambiaRiga(i, "fornitore", e.target.value)
                            }
                            placeholder="Fornitore"
                          />
                          <Input
                            value={r.nome}
                            onChange={(e) =>
                              cambiaRiga(i, "nome", e.target.value)
                            }
                            placeholder="Descrizione"
                          />
                          <Input
                            value={r.costo_totale}
                            onChange={(e) =>
                              cambiaRiga(i, "costo_totale", e.target.value)
                            }
                            placeholder="Imponibile"
                          />
                        </div>
                      ))}
                      <Button onClick={confermaDdt} disabled={busy}>
                        Conferma importazione
                      </Button>
                    </div>
                  )}
                </div>
                <Movimenti rows={detail?.ddt || []} ddt />
              </>
            )}
            {tab === "confronto" && (
              <div className="rounded-xl border bg-card p-6">
                <div className="flex items-center gap-3 mb-5">
                  <Search className="h-5 w-5" />
                  <div className="min-w-64">
                    <SearchableCombobox
                      value={fornitore}
                      onValueChange={setFornitore}
                      options={(detail?.fornitori || []).map((x) => ({
                        value: x,
                        label: x,
                      }))}
                      placeholder="Seleziona fornitore"
                      searchPlaceholder="Scrivi il fornitore..."
                    />
                  </div>
                </div>
                {confronto ? (
                  <div
                    className={`rounded-xl border-2 p-6 ${confronto.ok ? "border-emerald-500 bg-emerald-50" : "border-red-500 bg-red-50"}`}
                  >
                    <div className="grid sm:grid-cols-3 gap-4">
                      <Stat label="Totale fatture" value={confronto.fatture} />
                      <Stat label="Totale DDT" value={confronto.ddt} />
                      <Stat label="Differenza" value={confronto.differenza} />
                    </div>
                    <p
                      className={`font-bold mt-4 ${confronto.ok ? "text-emerald-700" : "text-red-700"}`}
                    >
                      {confronto.ok
                        ? "Importi corrispondenti"
                        : "Differenza da controllare"}
                    </p>
                  </div>
                ) : (
                  <p className="text-muted-foreground">
                    Seleziona un fornitore per confrontare fatture e DDT.
                  </p>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
function Stat({ label, value, strong }) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <p className="text-sm text-muted-foreground">{label}</p>
      <p
        className={`text-2xl font-bold mt-1 ${strong && Number(value) > 0 ? "text-destructive" : strong && Number(value) < 0 ? "text-emerald-600" : ""}`}
      >
        {eur(value)}
      </p>
    </div>
  );
}
function Movimenti({ rows, elimina, ddt, lavori }) {
  return (
    <div className="rounded-xl border bg-card overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Data</TableHead>
            <TableHead>Documento</TableHead>
            <TableHead>Descrizione</TableHead>
            <TableHead>
              {lavori ? "Cantiere" : "Fornitore / riferimento"}
            </TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead className="text-right">Importo</TableHead>
            {elimina && <TableHead />}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.length ? (
            rows.map((x) => (
              <TableRow key={x.id}>
                <TableCell>{(x.data || x.data_acquisto) ? String(x.data || x.data_acquisto).slice(0, 10).split("-").reverse().join("/") : "-"}</TableCell>
                <TableCell>
                  {x.numero_documento || x.numero_ddt || "-"}
                </TableCell>
                <TableCell>{x.descrizione || x.nome}</TableCell>
                <TableCell>
                  {lavori ? x.cantiere_nome : x.fornitore || "-"}
                </TableCell>
                <TableCell>
                  {lavori
                    ? "Lavoro"
                    : x.tipo_spesa || (ddt ? "DDT" : "Fattura collegata")}
                </TableCell>
                <TableCell className="text-right font-semibold">
                  {eur(x.importo ?? x.costo_totale)}
                </TableCell>
                {elimina && (
                  <TableCell>
                    {!x.automatica && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => elimina(x.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell
                colSpan={7}
                className="text-center py-10 text-muted-foreground"
              >
                Nessun dato registrato
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
